/**
 * PublicValidationStatus
 * Vista publica del estado de validacion del sistema eCRD.
 * Acceso por token (sin auth). Permite consultar fases firmadas y descargar PDFs.
 *
 * URL: /public/validation/:token
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, LockKey, FileText, ShieldCheck, Warning, Clock } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtNumber(n) {
  return new Intl.NumberFormat('es-ES').format(n || 0);
}

const STATUS_STYLES = {
  VALIDADO: {
    bg: 'bg-emerald-50 border-emerald-300',
    text: 'text-emerald-900',
    badge: 'bg-emerald-600',
    icon: ShieldCheck,
  },
  VALIDADO_PARCIAL: {
    bg: 'bg-blue-50 border-blue-300',
    text: 'text-blue-900',
    badge: 'bg-blue-600',
    icon: ShieldCheck,
  },
  EN_VALIDACION: {
    bg: 'bg-amber-50 border-amber-300',
    text: 'text-amber-900',
    badge: 'bg-amber-600',
    icon: Clock,
  },
};

export default function PublicValidationStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/validation/${token}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Error de acceso');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-stone-500">Cargando estado de validacion...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] p-6">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-md p-8 text-center" data-testid="public-validation-error">
          <Warning size={48} weight="fill" className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-stone-900 mb-2">Acceso denegado</h1>
          <p className="text-sm text-stone-600">{error}</p>
          <p className="text-xs text-stone-400 mt-4">
            Si el problema persiste, contacta con la CRO para obtener un nuevo enlace.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const statusStyle = STATUS_STYLES[data.overall_status] || STATUS_STYLES.EN_VALIDACION;
  const StatusIcon = statusStyle.icon;
  const progressPct = data.total_phases > 0
    ? Math.round((data.closed_count / data.total_phases) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="public-validation-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
              <ShieldCheck size={22} weight="bold" className="text-[#1f3e80]" />
              Estado de Validacion del Sistema
            </h1>
            <p className="text-xs text-stone-500 mt-0.5">
              {data.system.name} - {data.system.subtitle}
            </p>
          </div>
          <div className="text-right text-xs text-stone-400">
            <div>Acceso para: <span className="font-medium text-stone-600">{data.access_info.recipient || data.access_info.description}</span></div>
            <div>Expira: {fmtDate(data.access_info.expires_at)}</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Estado global */}
        <section className={`border rounded-md p-6 ${statusStyle.bg}`} data-testid="overall-status-card">
          <div className="flex items-start gap-4">
            <StatusIcon size={48} weight="fill" className={statusStyle.text} />
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <span className={`px-2.5 py-0.5 rounded text-xs font-semibold text-white ${statusStyle.badge}`}>
                  {data.overall_status.replace('_', ' ')}
                </span>
                <span className="text-sm font-medium text-stone-500">
                  {data.closed_count}/{data.total_phases} fases cerradas
                </span>
              </div>
              <h2 className={`text-xl font-semibold ${statusStyle.text}`}>
                {data.overall_status_label}
              </h2>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                {data.system.category} · Normativa: {data.system.regulations.join(' · ')}
              </p>
              <div className="w-full bg-white/70 rounded-full h-2 overflow-hidden mt-3">
                <div className={`h-2 ${statusStyle.badge} transition-all`} style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </div>
        </section>

        {/* Fases */}
        <section data-testid="phases-section">
          <h3 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">
            Plan de Validacion (VP-ECRD-001) - Estado de las 6 fases
          </h3>
          <div className="space-y-2">
            {data.phases.map((p) => {
              const isClosed = p.status === 'closed';
              return (
                <div
                  key={p.phase_id}
                  data-testid={`public-phase-${p.phase_id}`}
                  className={`bg-white border rounded-md p-4 flex items-start gap-3 ${isClosed ? 'border-emerald-200' : 'border-stone-200'}`}
                >
                  <div className="mt-0.5 shrink-0">
                    {isClosed
                      ? <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                      : <LockKey size={24} weight="bold" className="text-stone-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-medium text-stone-900">{p.label}</span>
                      {isClosed ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium">CERRADA</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-500 rounded font-medium">ABIERTA</span>
                      )}
                    </div>
                    <p className="text-xs text-stone-500">{p.activity}</p>
                    {isClosed && (
                      <div className="mt-2 text-xs text-stone-600 space-y-0.5 bg-emerald-50/40 border border-emerald-100 rounded px-2 py-1.5">
                        <div><span className="font-medium">Firmado por:</span> {p.signer_name || '-'}</div>
                        <div><span className="font-medium">Fecha:</span> {fmtDate(p.closed_at)}</div>
                        {p.reason && <div><span className="font-medium">Motivo:</span> {p.reason}</div>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Metricas en vivo */}
        <section className="bg-white border border-stone-200 rounded-md p-5" data-testid="metrics-section">
          <h3 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">
            Metricas del sistema (en vivo)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              ['Voluntarios', data.metrics.volunteers],
              ['Estudios activos', data.metrics.studies],
              ['Estudios totales', data.metrics.studies_total],
              ['Eventos en audit trail', data.metrics.audit_logs],
              ['Firmas electronicas', data.metrics.signatures],
              ['Usuarios con PIN', data.metrics.users_with_pin],
            ].map(([label, val]) => (
              <div key={label} className="bg-stone-50 rounded p-3">
                <div className="text-2xl font-bold text-[#1f3e80]">{fmtNumber(val)}</div>
                <div className="text-xs text-stone-500 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Documentos descargables */}
        <section data-testid="documents-section">
          <h3 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">
            Documentacion regulatoria descargable
          </h3>
          <div className="grid gap-2">
            {data.documents.map((d) => (
              <a
                key={d.id}
                href={`${API_URL}/api/public/validation/${token}/doc/${d.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`public-doc-${d.id}`}
                className="bg-white border border-stone-200 hover:border-[#1f3e80] hover:bg-[#1f3e80]/5 rounded-md p-3 flex items-center gap-3 transition-colors"
              >
                <FileText size={18} className="text-[#1f3e80] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900">{d.title}</div>
                  <div className="text-[10px] font-mono text-stone-500">{d.code}</div>
                </div>
                <span className="text-xs text-[#1f3e80] font-medium">Ver PDF</span>
              </a>
            ))}
          </div>
        </section>

        {/* Footer info */}
        <footer className="text-center text-xs text-stone-400 pt-4 pb-8 border-t border-stone-200 mt-8">
          <p>Estado generado el {fmtDate(data.generated_at)}</p>
          <p className="mt-1">Cada acceso a esta pagina queda registrado en audit_logs ALCOA+ por motivos de cumplimiento 21 CFR Part 11.</p>
        </footer>
      </main>
    </div>
  );
}
